/**
 * ElementoMapaRepository - Repositorio unificado para Elementos de Mapa
 *
 * Implementación compartida entre web (Dexie) y mobile (SQLite).
 *
 * Mismo modelo que LluviaRepository: delete() es SOFT-delete (marca
 * is_deleted=true y emite el UPDATE de sync), porque `elementos_mapa`
 * tiene la columna is_deleted en Supabase y así los borrados se
 * propagan offline vía UPDATE.
 *
 * Serialización de `geometria`: en Supabase es JSONB y en la cola de sync
 * viaja como OBJETO GeoJSON, pero en los caches locales se guarda como
 * STRING JSON (SQLite no soporta objetos como valor de columna). Este
 * repo normaliza en el límite:
 *   - Escrituras (add/update/bulkPutSilent): stringifica geometria para
 *     el adapter local, pero emite el cambio de sync con el OBJETO.
 *   - Lecturas (get/getAll/queries): parsea el string de vuelta a objeto.
 * Así los consumidores siempre operan con objetos GeoJSON.
 *
 * update / softDelete / restore emiten la FILA COMPLETA (caché local + cambios), igual que
 * SiembraLoteRepository: la cola offline se manda como upsert y un parcial falla NOT NULL.
 */

import { v4 as uuidv4 } from 'uuid';
import {
  IElementoMapaRepository,
  ElementoMapa,
  GeometriaMapa,
  FiltroElementosMapa
} from '../interfaces/IElementoMapaRepository';
import { BaseRepository } from './BaseRepository';
import { IDatabaseAdapter, WhereClause } from '../adapters/IDatabaseAdapter';
import { IProfileRepository } from '../interfaces/IProfileRepository';
import { normalizarGeometria } from '../../../utils/geoUtils';

/**
 * Repositorio unificado de Elementos de Mapa
 */
export class ElementoMapaRepository
  extends BaseRepository<ElementoMapa, string>
  implements IElementoMapaRepository
{
  private profileRepo: IProfileRepository | null = null;

  /** Clause reutilizable para excluir soft-deletes (is_deleted=false). */
  private readonly NOT_DELETED: WhereClause = { field: 'is_deleted', operator: 'equals', value: false };

  constructor(db: IDatabaseAdapter, profileRepo?: IProfileRepository) {
    super(db, 'elementos_mapa', 'elementos_mapa');

    // Habilitar funcionalidades de BaseRepository
    this.supportsSoftDelete = true;
    this.autoTimestamp = true;

    if (profileRepo) {
      this.profileRepo = profileRepo;
      this.setProfileRepository(profileRepo);
    }
  }

  // ==================== Normalización geometria ====================

  /**
   * geometria objeto → string JSON (para el adapter local), normalizada al
   * RFC 7946 (anillo cerrado + winding CCW): google.maps.Data rechaza
   * anillos abiertos — mobile dibujaba sin cerrar.
   */
  private toStorage(record: Record<string, any>): Record<string, any> {
    if (record.geometria !== undefined && record.geometria !== null && typeof record.geometria === 'object') {
      return { ...record, geometria: JSON.stringify(normalizarGeometria(record.geometria)) };
    }
    return record;
  }

  /** geometria string JSON → objeto normalizado (para los consumidores). */
  private fromStorage<T extends { geometria?: any }>(row: T): T {
    if (row && typeof row.geometria === 'string' && row.geometria) {
      try {
        return { ...row, geometria: normalizarGeometria(JSON.parse(row.geometria) as GeometriaMapa) };
      } catch (e) {
        console.warn('[ElementoMapaRepository] geometria corrupta, se ignora:', e);
      }
    }
    if (row && row.geometria && typeof row.geometria === 'object') {
      return { ...row, geometria: normalizarGeometria(row.geometria) };
    }
    return row;
  }

  private fromStorageList(rows: any[]): any[] {
    return rows.map(r => this.fromStorage(r));
  }

  // ==================== CRUD ====================

  async add(data: Omit<ElementoMapa, 'id'>): Promise<string> {
    const id = uuidv4();
    const now = this.getCurrentTimestamp();
    const profile = await this.getCurrentProfile();

    const record: ElementoMapa = {
      ...data,
      id,
      user_id: data.user_id || profile?.user_id || '',
      empresa_id: data.empresa_id || profile?.empresa_activa_id || '',
      created_at: data.created_at || now,
      updated_at: now,
      is_deleted: false,
      // RFC 7946 desde el origen: lo que viaja a Supabase (emit) y lo que se
      // guarda local (toStorage) deben ser EXACTAMENTE la misma geometría.
      geometria: data.geometria ? normalizarGeometria(data.geometria) : data.geometria
    };

    // Local: string; sync: objeto (JSONB en Supabase).
    await this.db.insert('elementos_mapa', this.toStorage(record));
    this.emitSync('INSERT', record);
    return id;
  }

  async update(data: Partial<ElementoMapa> & { id: string }): Promise<void> {
    await this.aplicarCambios(data.id, {
      ...data,
      updated_at: this.getCurrentTimestamp(),
      ...(data.geometria ? { geometria: normalizarGeometria(data.geometria) } : {})
    }, 'UPDATE');
  }

  /**
   * Soft-delete: marca is_deleted=true en lugar de borrar la fila.
   * El UPDATE viaja por la cola de sync y el borrado se propaga a
   * Supabase (y al resto de los dispositivos) aunque se haga offline.
   */
  async delete(id: string): Promise<void> {
    await this.softDelete(id);
  }

  /** Igual que BaseRepository.softDelete, pero el sync viaja con la fila completa. */
  async softDelete(id: string): Promise<void> {
    await this.aplicarCambios(id, { id, is_deleted: true, updated_at: this.getCurrentTimestamp() }, 'SOFT_DELETE');
  }

  /** Igual que BaseRepository.restore, pero el sync viaja con la fila completa. */
  async restore(id: string): Promise<void> {
    await this.aplicarCambios(id, { id, is_deleted: false, updated_at: this.getCurrentTimestamp() }, 'RESTORE');
  }

  /**
   * Escribe `cambios` en el caché local (sólo lo que cambió, geometria como string) y emite el
   * sync con la FILA COMPLETA: la fila local actual + los cambios, con geometria como OBJETO.
   * Offline la cola se manda como upsert (INSERT ... ON CONFLICT): un parcial sin empresa_id /
   * tipo / geometria lo rechaza Postgres por NOT NULL (23502) aunque la fila ya exista. Si la
   * fila no está en el caché no hay con qué completarla y se emite lo recibido.
   */
  private async aplicarCambios(
    id: string,
    cambios: Partial<ElementoMapa> & { id: string },
    accion: 'UPDATE' | 'SOFT_DELETE' | 'RESTORE'
  ): Promise<void> {
    const actual = await this.db.get('elementos_mapa', id);
    await this.db.update('elementos_mapa', id, this.toStorage(cambios));
    const completa = actual ? { ...this.fromStorage(actual as any), ...cambios } : cambios;
    this.emitSync(accion, completa as ElementoMapa);
  }

  /**
   * Override del PULL: las filas vienen de Supabase con geometria como
   * objeto JSONB; se stringifican antes de llegar al adapter local.
   * El evento local (refresco de UI) se emite con el objeto parseado.
   */
  async bulkPutSilent(items: any[]): Promise<void> {
    await this.db.bulkUpsert('elementos_mapa', items.map(i => this.toStorage(i)));

    items.forEach(item => {
      this.emitLocalSync('UPDATE', this.fromStorage(item));
    });
  }

  async get(id: string): Promise<ElementoMapa | undefined> {
    const row = await this.db.get('elementos_mapa', id);
    if (!row) return undefined;
    return this.fromStorage(row as any) as ElementoMapa;
  }

  async getAll(): Promise<ElementoMapa[]> {
    const rows = ((await this.db.getAll('elementos_mapa')) as any[]) || [];
    // Excluye soft-deletes (contrato de BaseRepository.getAll con
    // supportsSoftDelete). Chequeo falsy a propósito (patrón AnimalRepository):
    // en Dexie is_deleted puede llegar como 0/1 (hooks de normalización) y
    // `!== true` dejaría pasar los borrados guardados como 1.
    return this.fromStorageList(rows.filter(r => !r.is_deleted)) as ElementoMapa[];
  }

  // ==================== Queries ====================

  async getByFiltros(filtros: FiltroElementosMapa): Promise<ElementoMapa[]> {
    const clauses: WhereClause[] = [this.NOT_DELETED];

    if (filtros.empresa_id) {
      clauses.push({ field: 'empresa_id', operator: 'equals', value: filtros.empresa_id });
    }

    if (filtros.tipo) {
      clauses.push({ field: 'tipo', operator: 'equals', value: filtros.tipo });
    }

    const rows = await this.db.queryMultiple<ElementoMapa>('elementos_mapa', clauses, {
      column: 'created_at',
      direction: 'desc'
    });
    return this.fromStorageList(rows as any[]) as ElementoMapa[];
  }

  async getByEmpresa(empresaId: string): Promise<ElementoMapa[]> {
    const rows = await this.db.queryMultiple<ElementoMapa>('elementos_mapa', [
      this.NOT_DELETED,
      { field: 'empresa_id', operator: 'equals', value: empresaId }
    ], { column: 'created_at', direction: 'desc' });
    return this.fromStorageList(rows as any[]) as ElementoMapa[];
  }

  async getByTipo(empresaId: string, tipo: ElementoMapa['tipo']): Promise<ElementoMapa[]> {
    const rows = await this.db.queryMultiple<ElementoMapa>('elementos_mapa', [
      this.NOT_DELETED,
      { field: 'empresa_id', operator: 'equals', value: empresaId },
      { field: 'tipo', operator: 'equals', value: tipo }
    ], { column: 'created_at', direction: 'desc' });
    return this.fromStorageList(rows as any[]) as ElementoMapa[];
  }

  setProfileRepository(profileRepo: IProfileRepository): void {
    this.profileRepo = profileRepo;
    super.setProfileRepository(profileRepo);
  }
}
